import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Heart, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIERS = [
  { key: '25', label: '$25' }, { key: '50', label: '$50' }, { key: '100', label: '$100' },
  { key: '250', label: '$250' }, { key: '500', label: '$500' }, { key: '1000', label: '$1,000' },
];

export default function DonatePage() {
  const [tier, setTier] = useState('50');
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [coverFees, setCoverFees] = useState(false);
  const [campaign] = useState(() => new URLSearchParams(window.location.search).get('campaign') || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [payState, setPayState] = useState(null); // null | 'checking' | 'paid' | 'expired' | 'timeout' | 'cancelled'

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 10) { setPayState('timeout'); return; }
    try {
      const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
      if (data.payment_status === 'paid') { setPayState('paid'); return; }
      if (data.status === 'expired') { setPayState('expired'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPayState('timeout');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) { setPayState('checking'); pollStatus(sid); }
    else if (params.get('cancelled')) setPayState('cancelled');
  }, [pollStatus]);

  const donate = async () => {
    setError('');
    if (!name.trim() || !email.includes('@')) { setError('Please enter your name and a valid email.'); return; }
    if (tier === 'custom' && (!custom || Number(custom) < 1)) { setError('Please enter a custom amount of at least $1.'); return; }
    setBusy(true);
    try {
      const body = {
        donor_name: name.trim(),
        donor_email: email.trim(),
        note,
        cover_fees: coverFees,
        campaign,
        origin_url: window.location.origin,
      };
      if (tier === 'custom') body.custom_amount = Number(custom);
      else body.tier = tier;
      const { data } = await axios.post(`${API}/donations/checkout`, body);
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center px-4 py-10" data-testid="donate-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-block bg-white rounded-2xl px-5 py-3 shadow-sm mb-5">
            <img src="/tns-logo.png" alt="The Next Step Programs" className="h-12 w-auto object-contain" />
          </div>
          <h1 className="text-4xl font-medium text-[#2B1D3D]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
            Support The Next Step
          </h1>
          <p className="text-[#6B5E7D] mt-2">Your gift helps people with disabilities take their next step — every dollar goes to programs.</p>
          {campaign && (
            <span className="inline-block mt-2 px-3 py-1 rounded-full bg-[#6B3FA0]/10 text-[#6B3FA0] text-sm font-medium" data-testid="campaign-banner">
              Supporting: {campaign}
            </span>
          )}
        </div>

        {payState === 'checking' && (
          <Card data-testid="donate-checking"><CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0] mx-auto mb-3" />
            <p className="font-medium">Confirming your donation…</p>
          </CardContent></Card>
        )}

        {payState === 'paid' && (
          <Card data-testid="donate-success"><CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-2xl font-medium text-[#2B1D3D]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Thank you!</h2>
            <p className="text-[#6B5E7D] mt-2">Your donation was received. A receipt from Stripe is on its way to your email.</p>
            <Button className="mt-5" onClick={() => { window.location.href = '/donate'; }} data-testid="donate-again-btn">Make another gift</Button>
          </CardContent></Card>
        )}

        {(payState === 'expired' || payState === 'timeout') && (
          <Card data-testid="donate-problem"><CardContent className="p-8 text-center">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <p className="font-medium">{payState === 'expired' ? 'Your payment session expired.' : 'We could not confirm the payment yet.'}</p>
            <p className="text-sm text-[#6B5E7D] mt-1">{payState === 'expired' ? 'Please try again.' : 'If you completed payment, check your email for a Stripe receipt.'}</p>
            <Button className="mt-5" variant="outline" onClick={() => { window.location.href = '/donate'; }}>Back to donation form</Button>
          </CardContent></Card>
        )}

        {(payState === null || payState === 'cancelled') && (
          <Card>
            <CardContent className="p-6 space-y-5">
              {payState === 'cancelled' && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="donate-cancelled-banner">
                  Checkout was cancelled — no charge was made. You can try again below.
                </div>
              )}
              <div>
                <Label className="mb-2 block">Choose an amount</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="donate-tiers">
                  {TIERS.map(t => (
                    <button key={t.key} onClick={() => setTier(t.key)}
                            className={`py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${tier === t.key ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#2B1D3D] border-[#EAE5F0] hover:border-[#6B3FA0]'}`}
                            data-testid={`donate-tier-${t.key}`}>
                      {t.label}
                    </button>
                  ))}
                  <button onClick={() => setTier('custom')}
                          className={`py-2.5 rounded-full text-sm font-semibold border-2 col-span-3 transition-colors ${tier === 'custom' ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#2B1D3D] border-[#EAE5F0] hover:border-[#6B3FA0]'}`}
                          data-testid="donate-tier-custom">
                    Custom amount
                  </button>
                </div>
                {tier === 'custom' && (
                  <div className="mt-2 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B5E7D]">$</span>
                    <Input type="number" min="1" step="1" value={custom} onChange={e => setCustom(e.target.value)}
                           className="pl-7" placeholder="Enter amount" data-testid="donate-custom-amount" />
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="d-name">Your name</Label>
                  <Input id="d-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" data-testid="donate-name" />
                </div>
                <div>
                  <Label htmlFor="d-email">Email</Label>
                  <Input id="d-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" data-testid="donate-email" />
                </div>
              </div>
              <div>
                <Label htmlFor="d-note">Note (optional)</Label>
                <Textarea id="d-note" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="In honor of…" data-testid="donate-note" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="donate-error">{error}</p>}
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={coverFees} onChange={e => setCoverFees(e.target.checked)}
                       className="h-4 w-4 mt-0.5" data-testid="donate-fee-cover" />
                <span>Add 3% to cover card processing fees so 100% of my gift supports the mission</span>
              </label>
              <Button onClick={donate} disabled={busy} className="w-full h-11 text-base font-semibold" data-testid="donate-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Heart className="h-4 w-4 mr-1" fill="currentColor" /> Donate securely with Stripe</>}
              </Button>
              <p className="text-[11px] text-center text-[#9A8CA8]">Payments are processed securely by Stripe. We never see your card details.</p>
            </CardContent>
          </Card>
        )}
        <p className="text-[10px] text-gray-400 text-center mt-8 leading-relaxed" data-testid="donate-charity-disclosure">
          The official registration and financial information of The Next Step Programs may be obtained from the Pennsylvania Department of State by calling toll-free, within Pennsylvania, 1-800-732-0999. Registration does not imply endorsement.
        </p>
      </div>
    </div>
  );
}
